import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import classes from "./createPackages.module.css";
import {
  createArtistPackage,
  deleteArtistPackage,
  fetchArtistPackage,
  updateArtistPackage,
} from "../../../../redux/slices/packageSlice";
import Cookies from "js-cookie";

const CreatePackages = () => {
  const dispatch = useDispatch();
  const { packages, loading, error } = useSelector((state) => state.package);

  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [newPackage, setNewPackage] = useState({
    packageName: "",
    duration: "",
    price: "",
    details: [""],
    description: "",
  });

  const artist = Cookies.get("user");
  const artistParsed = JSON.parse(artist);

  useEffect(() => {
    dispatch(fetchArtistPackage({ artistUsername: artistParsed.username }));
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPackage({
      ...newPackage,
      [name]: value,
    });
  };

  const handleDetailChange = (index, value) => {
    const updatedDetails = [...newPackage.details];
    updatedDetails[index] = value;
    setNewPackage({
      ...newPackage,
      details: updatedDetails,
    });
  };

  const addDetailField = () => {
    setNewPackage({
      ...newPackage,
      details: [...newPackage.details, ""],
    });
  };

  const removeDetailField = (index) => {
    const updatedDetails = newPackage.details.filter((_, i) => i !== index);
    setNewPackage({
      ...newPackage,
      details: updatedDetails,
    });
  };

  const savePackage = () => {
    const filteredDetails = newPackage.details.filter(
      (detail) => detail.trim() !== ""
    );

    if (isEditing) {
      const packageToUpdate = {
        packageId: editingPackageId,
        packageName: newPackage.packageName,
        duration: Number(newPackage.duration),
        price: Number(newPackage.price),
        details: filteredDetails,
        description: newPackage.description,
      };

      dispatch(updateArtistPackage(packageToUpdate));
      setTimeout(() => {
        dispatch(fetchArtistPackage({ artistUsername: artistParsed.username }));
      }, 1500);
    } else {
      const packageToCreate = {
        packageName: newPackage.packageName,
        duration: Number(newPackage.duration),
        price: Number(newPackage.price),
        details: filteredDetails,
        description: newPackage.description,
      };

      dispatch(createArtistPackage(packageToCreate));
      setTimeout(() => {
        dispatch(fetchArtistPackage({ artistUsername: artistParsed.username }));
      }, 1500);
    }

    // Reset form
    setIsCreating(false);
    setIsEditing(false);
    setEditingPackageId(null);
    setNewPackage({
      packageName: "",
      duration: "",
      price: "",
      details: [""],
      description: "",
    });
  };

  const cancelCreation = () => {
    setIsCreating(false);
    setIsEditing(false);
    setEditingPackageId(null);
    setNewPackage({
      packageName: "",
      duration: "",
      price: "",
      details: [""],
      description: "",
    });
  };

  const startEditing = (packageId) => {
    const packageToEdit = packages.find((pkg) => pkg._id === packageId);
    if (packageToEdit) {
      setNewPackage({
        packageName: packageToEdit.packageName,
        duration: packageToEdit.duration.toString(),
        price: packageToEdit.price.toString(),
        details:
          packageToEdit.details.length > 0 ? [...packageToEdit.details] : [""],
        description: packageToEdit.description || "",
      });
      setIsEditing(true);
      setEditingPackageId(packageId);
      setIsCreating(true);
    }
  };

  const deletePackage = (packageId) => {
    dispatch(deleteArtistPackage({ packageId: packageId }));

    setTimeout(() => {
      dispatch(fetchArtistPackage({ artistUsername: artistParsed.username }));
    }, 1500);
  };

  return (
    <div className={classes.container}>
      <h2 className={classes.heading}>Manage Service Packages</h2>

      {loading && <p>Loading packages...</p>}
      {error && <p className={classes.errorMessage}>Error: {error}</p>}

      {!isCreating ? (
        <div>
          <div className={classes.packageGrid}>
            {packages && packages.length > 0 ? (
              packages.map((pkg) => (
                <div key={pkg._id} className={classes.packageCard}>
                  <h3 className={classes.packageTitle}>{pkg.packageName}</h3>
                  <div className={classes.packageMeta}>
                    <span>Duration: {pkg.duration} hours</span>
                    <span>Price: ₹{pkg.price}</span>
                  </div>
                  <h4 className={classes.detailsHeading}>What's Included:</h4>
                  <ul className={classes.detailsList}>
                    {pkg.details &&
                      pkg.details.map((detail, index) => (
                        <li key={index}>{detail}</li>
                      ))}
                  </ul>
                  <p className={classes.packageDescription}>
                    {pkg.description}
                  </p>
                  <div className={classes.cardActions}>
                    <button
                      className={`${classes.button} ${classes.editButton}`}
                      onClick={() => startEditing(pkg._id)}
                    >
                      Edit
                    </button>
                    <button
                      className={`${classes.button} ${classes.deleteButton}`}
                      onClick={() => deletePackage(pkg._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p>No packages found. Create your first package!</p>
            )}

            <div
              className={classes.addCardPlaceholder}
              onClick={() => setIsCreating(true)}
            >
              <div className={classes.addIcon}>+</div>
              <p>Create New Package</p>
            </div>
          </div>
        </div>
      ) : (
        <div className={classes.createForm}>
          <h3 className={classes.formTitle}>
            {isEditing ? "Edit Package" : "Create New Package"}
          </h3>

          <div className={classes.formGroup}>
            <label htmlFor="packageName" className={classes.label}>
              Package Name
            </label>
            <input
              type="text"
              id="packageName"
              name="packageName"
              value={newPackage.packageName}
              onChange={handleInputChange}
              className={classes.input}
              placeholder="e.g. Wedding Package"
            />
          </div>

          <div className={classes.formRow}>
            <div className={classes.formGroup}>
              <label htmlFor="duration" className={classes.label}>
                Duration (hours)
              </label>
              <input
                type="number"
                id="duration"
                name="duration"
                value={newPackage.duration}
                onChange={handleInputChange}
                className={classes.input}
                placeholder="e.g. 3"
              />
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="price" className={classes.label}>
                Price (₹)
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={newPackage.price}
                onChange={handleInputChange}
                className={classes.input}
                placeholder="e.g. 8000"
              />
            </div>
          </div>

          <div className={classes.formGroup}>
            <label className={classes.label}>Package Details</label>
            {newPackage.details.map((detail, index) => (
              <div key={index} className={classes.detailField}>
                <input
                  type="text"
                  value={detail}
                  onChange={(e) => handleDetailChange(index, e.target.value)}
                  className={classes.input}
                  placeholder="e.g. Equipments provided"
                />
                <button
                  type="button"
                  onClick={() => removeDetailField(index)}
                  className={classes.removeButton}
                  disabled={newPackage.details.length === 1}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addDetailField}
              className={classes.addButton}
            >
              + Add Another Detail
            </button>
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="description" className={classes.label}>
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={newPackage.description}
              onChange={handleInputChange}
              className={classes.textarea}
              placeholder="Describe what makes this package special..."
              rows={4}
            />
          </div>

          <div className={classes.formActions}>
            <button
              type="button"
              onClick={cancelCreation}
              className={`${classes.button} ${classes.cancelButton}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={savePackage}
              className={`${classes.button} ${classes.saveButton}`}
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : isEditing
                ? "Update Package"
                : "Save Package"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePackages;
